import { useState, useCallback } from 'react';
import { useMatrixClient } from '../context/MatrixContext';
import logger from '../utils/logger';
import { invalidateAvatar } from '../utils/avatarManager';
import { ALLOWED_IMAGE_TYPES, MAX_AVATAR_SIZE_BYTES } from '../constants';

/**
 * Hook for managing user profile operations
 * Handles avatar upload, display name changes, and profile updates
 * 
 * @returns {Object} Profile management functions and state
 */
export const useProfile = () => {
  const client = useMatrixClient();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  /**
   * Upload and set user avatar
   * @param {File} file - Image file to upload as avatar
   * @returns {Promise<string>} MXC URL of uploaded avatar
   */
  const uploadAvatar = useCallback(async (file) => {
    if (!client) {
      throw new Error('Matrix client not available');
    }

    if (!file) {
      throw new Error('No file provided');
    }

    // Validate file type - only allow specific image types for security
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      throw new Error('INVALID_FILE_TYPE');
    }

    // Validate file size
    if (file.size > MAX_AVATAR_SIZE_BYTES) {
      throw new Error('FILE_TOO_LARGE');
    }

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      logger.debug('Uploading avatar:', file.name, file.type);

      // Upload the file to Matrix content repository
      const uploadResponse = await client.uploadContent(file, {
        name: file.name,
        type: file.type,
        progressHandler: (progress) => {
          if (progress.total > 0) {
            const percentage = Math.round((progress.loaded / progress.total) * 100);
            setUploadProgress(percentage);
          }
        },
      });

      // Extract MXC URL from response
      const mxcUrl = uploadResponse.content_uri || uploadResponse;
      logger.debug('Avatar uploaded, MXC URL:', mxcUrl);

      // Invalidate cache BEFORE setting avatar to prevent race condition
      const userId = client.getUserId();
      await invalidateAvatar(userId);

      // Set the avatar URL in user's profile
      await client.setAvatarUrl(mxcUrl);
      logger.info('Avatar set successfully:', mxcUrl);

      setIsUploading(false);
      setUploadProgress(100);

      // Emit custom event to trigger AvatarSync to reload
      // This works even when Matrix presence is disabled
      window.dispatchEvent(new CustomEvent('matrix:avatar:changed', {
        detail: { userId, mxcUrl },
      }));
      logger.debug('Avatar change event emitted for:', userId);

      return mxcUrl;
    } catch (err) {
      logger.error('Failed to upload avatar:', err);
      setError(err.message || 'Failed to upload avatar');
      setIsUploading(false);
      throw err;
    }
  }, [client]);

  /**
   * Update user's display name
   * @param {string} displayName - New display name
   * @returns {Promise<void>}
   */
  const updateDisplayName = useCallback(async (displayName) => {
    if (!client) {
      throw new Error('Matrix client not available');
    }

    if (!displayName || !displayName.trim()) {
      throw new Error('Display name cannot be empty');
    }

    try {
      logger.debug('Updating display name to:', displayName);
      await client.setDisplayName(displayName.trim());
      logger.info('Display name updated successfully');
    } catch (err) {
      logger.error('Failed to update display name:', err);
      throw err;
    }
  }, [client]);

  /**
   * Get current user's profile information
   * @returns {Promise<Object>} Profile information (displayname, avatar_url)
   */
  const getProfile = useCallback(async () => {
    if (!client) {
      throw new Error('Matrix client not available');
    }

    try {
      const userId = client.getUserId();
      const profile = await client.getProfileInfo(userId);
      
      // Convert MXC URL to HTTP URL for avatar if present
      // Using Matrix v1 authenticated media endpoints (Matrix 1.11+ standard)
      let avatarHttpUrl = null;
      if (profile.avatar_url) {
        avatarHttpUrl = client.mxcUrlToHttp(
          profile.avatar_url,
          96,   // width
          96,   // height
          'crop',
          false, // allowDirectLinks (security: always false)
          true,  // allowRedirects (required for authenticated media)
          true,   // useAuthentication (required for Matrix 1.11+)
        );
      }

      return {
        displayName: profile.displayname || '',
        avatarUrl: profile.avatar_url || null,
        avatarHttpUrl,
      };
    } catch (err) {
      logger.error('Failed to get profile:', err);
      throw err;
    }
  }, [client]);

  /**
   * Clear/remove user's avatar
   * @returns {Promise<void>}
   */
  const removeAvatar = useCallback(async () => {
    if (!client) {
      throw new Error('Matrix client not available');
    }

    try {
      logger.debug('Removing avatar');

      // Invalidate cache BEFORE removing avatar to prevent race condition
      const userId = client.getUserId();
      await invalidateAvatar(userId);

      await client.setAvatarUrl(''); // Empty string removes avatar
      logger.info('Avatar removed successfully');

      // Emit custom event to trigger AvatarSync to reload
      // This works even when Matrix presence is disabled
      window.dispatchEvent(new CustomEvent('matrix:avatar:changed', {
        detail: { userId, mxcUrl: null },  // null indicates avatar removed
      }));
      logger.debug('Avatar removal event emitted for:', userId);
    } catch (err) {
      logger.error('Failed to remove avatar:', err);
      throw err;
    }
  }, [client]);

  return {
    // State
    isUploading,
    uploadProgress,
    error,

    // Actions
    uploadAvatar,
    updateDisplayName,
    getProfile,
    removeAvatar,
  };
};

export default useProfile;
